/**
 * The driver qualification requirement catalog.
 *
 * TypeScript is the source of truth. It is reviewable in a diff, testable without a
 * database, and versioned in git alongside the reasoning — all of which matter more here
 * than for ordinary config, because these entries are assertions about federal
 * regulation. A copy is seeded into Postgres so evaluations can reference a catalog
 * version and stay reproducible, but the rules live here.
 *
 * VERSIONING: never edit a published entry's meaning in place. Bump CATALOG_VERSION and
 * add the corrected entry. Historical evaluations must remain reproducible — if James is
 * asked in a deposition what this system reported on a given date, the answer has to be
 * recoverable, and that is impossible if the rulebook was silently rewritten.
 *
 * Every entry carries its CFR citation. The UI always shows it, so a carrier can check
 * our work rather than taking it on faith.
 *
 * Verified against the CFR and FMCSA guidance as of July 2026.
 */
export const CATALOG_VERSION = 1;
const ALL = () => true;
const IS_CDL = (d) => d.driverClass === 'cdl' || d.driverClass === 'clp';
const IS_NON_CDL = (d) => d.driverClass === 'non_cdl';
export const REQUIREMENT_CATALOG = [
    // ── At hire, once ──────────────────────────────────────────────────────────────
    {
        code: 'EMPLOYMENT_APPLICATION',
        title: "Driver's application for employment",
        cfrCite: '49 CFR 391.21',
        description: 'The completed application, including the 3-year address and employment history the driver must supply.',
        artifactKind: 'document',
        responsibleParty: 'either',
        sensitivity: 'normal',
        recurrence: 'once_at_hire',
        retention: 'employment_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    {
        code: 'PRE_EMPLOYMENT_MVR',
        title: 'Pre-employment motor vehicle record',
        cfrCite: '49 CFR 391.23(a)(1)',
        description: 'An MVR from every state where the driver held a licence or permit in the previous 3 years.',
        note: 'Must be requested within 30 days of the driver starting work. A driver licensed in more than one state in the last 3 years needs an MVR from each.',
        artifactKind: 'document',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'within_days_of_hire',
        dueWithinDaysOfHire: 30,
        retention: 'employment_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    {
        code: 'ROAD_TEST',
        title: 'Road test certificate or accepted equivalent',
        cfrCite: '49 CFR 391.31(e)',
        description: 'A road test certificate issued by the carrier, or an equivalent accepted under §391.33 such as a valid CDL.',
        note: 'Under §391.33 a carrier may accept a valid CDL in place of a road test for most vehicles. If you do, file the copy of the licence you relied on and the written acceptance.',
        artifactKind: 'document',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'once_at_hire',
        retention: 'employment_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    {
        code: 'SAFETY_PERFORMANCE_HISTORY',
        title: 'Previous employer safety performance investigations',
        cfrCite: '49 CFR 391.23(d)-(e)',
        description: 'Investigation of the driver’s safety performance with every DOT-regulated employer in the previous 3 years, including the drug and alcohol information required by §40.25.',
        note: 'The investigation must be completed within 30 days of the driver starting work. Good-faith attempts must be documented when a previous employer does not respond — the record of trying is what an auditor looks for.',
        artifactKind: 'record',
        responsibleParty: 'carrier',
        sensitivity: 'restricted',
        recurrence: 'within_days_of_hire',
        dueWithinDaysOfHire: 30,
        retention: 'employment_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    // ── Annual ────────────────────────────────────────────────────────────────────
    {
        code: 'ANNUAL_MVR',
        title: 'Annual motor vehicle record',
        cfrCite: '49 CFR 391.25(a)',
        description: 'An MVR obtained at least once every 12 months from each licensing state.',
        artifactKind: 'document',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'annual',
        validForMonths: 12,
        retention: 'execution_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    {
        code: 'ANNUAL_REVIEW',
        title: 'Annual review of driving record',
        cfrCite: '49 CFR 391.25(c)(2)',
        description: 'A dated note, signed by the carrier, recording that the driving record was reviewed and that the driver still meets the qualification standards.',
        note: 'This is a separate requirement from obtaining the MVR. Carriers are routinely cited for holding the annual MVR with no evidence anyone reviewed it.',
        artifactKind: 'attestation',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'annual',
        validForMonths: 12,
        retention: 'execution_plus_3y',
        appliesTo: ALL,
        disqualifying: false,
    },
    // ── Medical certification — the CDL / non-CDL split ────────────────────────────
    //
    // The Medical Examiner's Certification Integration rule took effect 2025-06-23.
    // Certified medical examiners now transmit results electronically to the FMCSA
    // National Registry, which passes them to the State Driver Licensing Agency, which
    // posts the certification status to the driver's MVR.
    //
    // The consequence, and the reason these are two separate requirements: for a CDL or CLP
    // holder a copy of the paper certificate is no longer valid proof, and NRCME
    // verification is no longer required of the carrier. For a non-CDL driver nothing
    // changed. Software that files a med card for every driver leaves CDL files deficient;
    // software that demands an MVR for every driver flags non-CDL files that are fine.
    {
        code: 'MED_CERT_MVR_CDL',
        title: 'MVR showing current medical certification status',
        cfrCite: '49 CFR 391.51(b)(7)(ii)',
        description: 'For CDL and CLP holders, an MVR reflecting the medical certification status posted by the state after the most recent examination.',
        note: 'Changed 23 June 2025. Medical examiners transmit results to the FMCSA National Registry and the state posts them to the MVR, so a copy of the paper Medical Examiner’s Certificate is no longer valid proof for a CDL or CLP holder, and you are no longer required to verify the examiner against the National Registry. Obtain a fresh MVR after each examination.',
        artifactKind: 'document',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'per_medical_exam',
        // Certificates run up to 24 months, but shorter windows are common when a condition
        // is being monitored. The artifact's own expiry date wins; this is only the fallback.
        validForMonths: 24,
        retention: 'execution_plus_3y',
        appliesTo: (d) => IS_CDL(d) && !d.hasMedicalExemption,
        disqualifying: true,
    },
    {
        code: 'MED_CERT_PAPER_NON_CDL',
        title: "Medical examiner's certificate (copy)",
        cfrCite: '49 CFR 391.43(g)',
        description: 'For non-CDL drivers, a legible copy of the Medical Examiner’s Certificate held in the file.',
        note: 'Unchanged by the June 2025 rule. Non-CDL drivers still provide a paper certificate, and the carrier still keeps a copy and verifies the examiner on the National Registry.',
        artifactKind: 'document',
        responsibleParty: 'driver',
        sensitivity: 'normal',
        recurrence: 'per_medical_exam',
        validForMonths: 24,
        retention: 'execution_plus_3y',
        appliesTo: (d) => IS_NON_CDL(d) && !d.hasMedicalExemption,
        disqualifying: true,
    },
    {
        code: 'NRCME_VERIFICATION',
        title: 'National Registry examiner verification note',
        cfrCite: '49 CFR 391.23(m)',
        description: 'A dated note recording that the medical examiner who signed the certificate was listed on the National Registry of Certified Medical Examiners.',
        note: 'Required for non-CDL drivers only. As of 23 June 2025 carriers no longer verify the examiner for CDL and CLP holders, because certification now reaches the carrier through the MVR rather than the certificate.',
        artifactKind: 'attestation',
        responsibleParty: 'carrier',
        sensitivity: 'normal',
        recurrence: 'per_medical_exam',
        validForMonths: 24,
        retention: 'execution_plus_3y',
        appliesTo: (d) => IS_NON_CDL(d) && !d.hasMedicalExemption,
        disqualifying: false,
    },
    {
        code: 'SPE_CERTIFICATE',
        title: 'Skill Performance Evaluation certificate',
        cfrCite: '49 CFR 391.49',
        description: 'The SPE certificate for a driver with a fixed deficit of a limb, where one has been issued.',
        artifactKind: 'document',
        responsibleParty: 'driver',
        sensitivity: 'normal',
        // The certificate carries its own expiry; there is no interval to compute.
        recurrence: 'artifact_expiry',
        retention: 'execution_plus_3y',
        appliesTo: (d) => d.hasSpeCertificate,
        disqualifying: true,
    },
    {
        code: 'MEDICAL_EXEMPTION',
        title: 'Federal medical exemption document',
        cfrCite: '49 CFR part 381',
        description: 'The FMCSA exemption letter for a driver operating under a federal medical exemption, such as vision or diabetes.',
        note: 'Where an exemption applies it substitutes for the ordinary medical certification item, which is why this requirement and the medical certification requirements are mutually exclusive.',
        artifactKind: 'document',
        responsibleParty: 'driver',
        sensitivity: 'normal',
        // The exemption letter states its own validity period.
        recurrence: 'artifact_expiry',
        retention: 'execution_plus_3y',
        appliesTo: (d) => d.hasMedicalExemption,
        disqualifying: true,
    },
    // ── Drug and Alcohol Clearinghouse — §382.701 ─────────────────────────────────
    {
        code: 'CLEARINGHOUSE_PRE_EMPLOYMENT',
        title: 'Clearinghouse pre-employment full query',
        cfrCite: '49 CFR 382.701(a)',
        description: 'A full query of the FMCSA Drug and Alcohol Clearinghouse, run with the driver’s electronic consent before they perform any safety-sensitive function.',
        artifactKind: 'record',
        responsibleParty: 'carrier',
        sensitivity: 'restricted',
        recurrence: 'pre_employment',
        retention: 'query_plus_3y',
        appliesTo: IS_CDL,
        disqualifying: true,
    },
    {
        code: 'CLEARINGHOUSE_ANNUAL',
        title: 'Clearinghouse annual limited query',
        cfrCite: '49 CFR 382.701(b)',
        description: 'A limited query run at least once in every 365-day period for each CDL driver, with the driver’s written consent on file.',
        note: 'If a limited query comes back showing that information exists, you must run a full query within 24 hours or remove the driver from safety-sensitive functions. This platform raises that 24-hour deadline as its own alert.',
        artifactKind: 'record',
        responsibleParty: 'carrier',
        sensitivity: 'restricted',
        recurrence: 'rolling_365',
        validForDays: 365,
        retention: 'query_plus_3y',
        appliesTo: IS_CDL,
        disqualifying: true,
    },
    {
        code: 'DA_PRE_EMPLOYMENT_TEST',
        title: 'Pre-employment controlled substances test',
        cfrCite: '49 CFR 382.301(a)',
        description: 'A verified negative pre-employment controlled substances result, received before the driver first performs a safety-sensitive function.',
        note: 'Held as a restricted record: drug and alcohol testing records must be kept confidential and separate from the ordinary qualification file under §40.331 and §382.401.',
        artifactKind: 'record',
        responsibleParty: 'carrier',
        sensitivity: 'restricted',
        recurrence: 'pre_employment',
        // Negative and cancelled results: 1 year — §382.401(b)(1)(vi). Positives, refusals and
        // alcohol results of 0.02 or greater run 5 years from the test date and are handled by
        // the drug-and-alcohol module rather than the qualification file.
        retention: 'test_date_plus_1y',
        appliesTo: IS_CDL,
        disqualifying: true,
    },
    // ── Licence validity ──────────────────────────────────────────────────────────
    {
        code: 'CDL_VALID',
        title: 'Valid commercial driver’s licence',
        cfrCite: '49 CFR 391.11(b)(5)',
        description: 'The driver holds a current, valid CDL or CLP for the vehicle being operated, with expiry tracked.',
        note: '§391.51 does not require a copy of the licence in the qualification file. This item exists because §391.11(b)(5) requires the licence to be valid, and an expired licence disqualifies the driver immediately — so the expiry date is worth tracking even though the copy itself is not mandated.',
        artifactKind: 'document',
        responsibleParty: 'driver',
        sensitivity: 'normal',
        // Validity is the expiry printed on the licence.
        recurrence: 'artifact_expiry',
        retention: 'employment_plus_3y',
        appliesTo: IS_CDL,
        disqualifying: true,
    },
];
/** Lookup by code. Built once. */
export const REQUIREMENTS_BY_CODE = new Map(REQUIREMENT_CATALOG.map((r) => [r.code, r]));
export function requirementsFor(driver) {
    return REQUIREMENT_CATALOG.filter((r) => r.appliesTo(driver));
}
//# sourceMappingURL=catalog.js.map